import React, { useCallback, useEffect, useState } from "react";
import { CreditCard, Gauge, Phone, MessageSquare, ShieldCheck, Sparkles } from "lucide-react";
import { PageHeader, Button, StatusBadge, LoadingBlock, toast } from "@/components/ds";
import { api, formatApiError } from "@/lib/api";

const STATUS_TONE: Record<string, "green" | "amber" | "red" | "slate" | "blue"> = {
  active: "green", inactive: "slate", trialing: "blue", past_due: "amber", cancel_at_period_end: "amber", cancelled: "red", expired: "red",
};
const money = (n: number | null | undefined) => (n == null ? "—" : `$${Number(n).toLocaleString()}`);

export default function Billing() {
  const [data, setData] = useState<any>(null);
  const [plans, setPlans] = useState<any[]>([]);
  const [stripe, setStripe] = useState<{ configured: boolean; tax_mode: string } | null>(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => {
    api.billingSubscription().then(setData).catch((e) => toast.error(formatApiError(e)));
    api.publicPlans().then(setPlans).catch(() => {});
    api.billingStripeStatus().then(setStripe).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Return from Stripe Checkout: sync + toast, then clean the URL.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const ck = p.get("checkout");
    if (!ck) return;
    if (ck === "success" && p.get("session_id")) {
      api.billingCheckoutStatus(p.get("session_id") as string)
        .then((r) => { toast.success(r.paid ? "Subscription active — thank you!" : "Payment processing…"); load(); })
        .catch((e) => toast.error(formatApiError(e)));
    } else if (ck === "cancel") {
      toast.info("Checkout canceled — no changes made.");
    }
    window.history.replaceState({}, "", "/app/billing");
  }, [load]);

  const select = async (plan_type: string) => {
    if (!stripe?.configured) {
      try { setData(await api.billingSelectPlan(plan_type)); toast.success("Plan updated (demo)"); }
      catch (e) { toast.error(formatApiError(e)); }
      return;
    }
    setBusy(plan_type);
    try { const { checkout_url } = await api.billingCheckout(plan_type); window.location.href = checkout_url; }
    catch (e) { toast.error(formatApiError(e)); setBusy(""); }
  };
  const openPortal = async () => {
    if (!stripe?.configured) { const r = await api.billingPortal(); toast.info(r.message); return; }
    try { const { portal_url } = await api.billingPortalSession(); window.location.href = portal_url; }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const contactSales = async () => {
    try { const r = await api.billingContactSales({ message: "Interested in a Custom plan." }); toast.success(r.note || "Recorded"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!data) return <div className="p-2"><LoadingBlock rows={6} /></div>;
  const { subscription: sub, entitlements: ent, usage, access_state } = data;

  return (
    <div data-testid="page-billing" className="space-y-5">
      <PageHeader title="Billing & Usage" testid="billing-header"
        description="Your plan, included usage and setup fee. Billing is in demo mode — no charges are made yet." />

      {stripe?.configured
        ? <div className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800" data-testid="billing-stripe-banner">
            Live Stripe checkout (test mode). Choosing a plan opens Stripe Checkout with a one-time setup fee.
            Tax mode: <b>{stripe.tax_mode.replace("_", " ")}</b>. Use test card 4242 4242 4242 4242.
          </div>
        : <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="billing-demo-banner">
            Demo / Simulated billing — Stripe is not connected. Plan changes here update entitlements only; no card is charged.
          </div>}

      {access_state === "past_due" && (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800" data-testid="billing-pastdue-alert">
          <span>Your last payment didn't go through.{data?.ai_access ? " Your AI receptionist stays active during the grace period — please update your card to avoid interruption." : " Your AI receptionist is paused until payment is updated."}</span>
          <Button size="sm" variant="outline" onClick={openPortal} data-testid="billing-pastdue-fix">Update payment method</Button>
        </div>
      )}

      {/* Current plan */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="billing-current-plan">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2 text-sm font-semibold text-slate-800"><CreditCard className="h-4 w-4 text-blue-500" /> Current plan</div>
            <StatusBadge tone={STATUS_TONE[access_state] ?? "slate"} data-testid="billing-status">{String(access_state).replace(/_/g, " ")}</StatusBadge>
          </div>
          <p className="font-display text-2xl font-semibold capitalize text-slate-900" data-testid="billing-plan-name">{sub.plan_type}</p>
          {sub.promotion_type === "growth_intro_25" && (
            <div className="mt-2 rounded-lg border border-emerald-100 bg-emerald-50 p-3" data-testid="billing-promo">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-emerald-800">
                <Sparkles className="h-3.5 w-3.5" /> {sub.promotion_label || "Growth Intro — 25% Off First Month"}
              </p>
              <div className="mt-1.5 space-y-0.5 text-xs text-slate-600">
                <div className="flex justify-between"><span>Promotional first month</span><span className="font-medium text-slate-800">{money((sub.promo_discounted_first_month_cents ?? 0) / 100)}</span></div>
                <div className="flex justify-between"><span>One-time setup fee</span><span className="font-medium text-slate-800">{money((sub.promo_discounted_setup_fee_cents ?? 0) / 100)}</span></div>
                <div className="flex justify-between border-t border-emerald-100 pt-0.5"><span>Total initial payment</span><span className="font-semibold text-emerald-700">{money((sub.promo_initial_payment_cents ?? 0) / 100)}</span></div>
                <div className="flex justify-between"><span>Standard recurring</span><span className="font-medium text-slate-800">{money((sub.promo_standard_recurring_monthly_cents ?? 0) / 100)}/mo</span></div>
              </div>
              <p className="mt-1.5 text-[11px] text-slate-500">Standard recurring billing begins with the second month. Promo code: <code className="text-slate-600">growth_intro_25</code></p>
            </div>
          )}
          <div className="mt-1 text-sm text-slate-600">
            {ent.monthly_price != null ? <>{money(ent.monthly_price)} / month</> : "Custom pricing"}
            <span className="text-slate-400"> · setup {ent.effective_setup_fee === 0 ? <b className="text-emerald-600">WAIVED</b> : money(ent.effective_setup_fee)}
              {ent.setup_fee_status && ent.setup_fee_status !== "standard" ? <span className="ml-1 text-xs">({ent.setup_fee_status})</span> : null}</span>
          </div>
          {sub.status === "inactive" && (
            <p className="mt-2 text-xs text-slate-400">No active subscription — choose a plan to activate your AI receptionist.</p>
          )}
          <div className="mt-4 flex gap-2">
            {sub.cancel_at_period_end
              ? <Button size="sm" variant="outline" onClick={async () => setData(await api.billingResume())} data-testid="billing-resume">Resume</Button>
              : <Button size="sm" variant="outline" onClick={async () => setData(await api.billingCancel())} data-testid="billing-cancel">Cancel at period end</Button>}
            <Button size="sm" variant="ghost" onClick={async () => { const r = await api.billingPortal(); toast.info(r.message); }} data-testid="billing-portal">Billing portal</Button>
          </div>
        </div>

        {/* Usage */}
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="billing-usage">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Gauge className="h-4 w-4 text-indigo-500" /> This period's usage</div>
          <UsageBar icon={Phone} label="AI Voice" unit="minutes" m={usage.voice_minutes} testid="usage-voice" />
          <div className="h-3" />
          <UsageBar icon={MessageSquare} label="SMS" unit="messages" m={usage.sms} testid="usage-sms" />
          <p className="mt-3 text-xs text-slate-400">Backend is the source of truth. Overage shown for visibility only — not billed in this release.</p>
        </div>
      </div>

      {/* Plan chooser (demo) */}
      <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="billing-plans">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Sparkles className="h-4 w-4 text-blue-500" /> Change plan</div>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {plans.map((p) => {
            const current = p.plan_type === sub.plan_type;
            const custom = p.custom_pricing || p.contact_sales;
            return (
              <div key={p.plan_type} className={`rounded-lg border p-3 ${p.popular ? "border-blue-500" : "border-slate-200"}`} data-testid={`billing-plan-${p.plan_type}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-slate-800">{p.name}</span>
                  {p.popular && <StatusBadge tone="blue">Popular</StatusBadge>}
                </div>
                <p className="mt-1 text-sm text-slate-600">{custom ? "Custom pricing" : `${money(p.monthly_price)}/mo`}</p>
                {!custom && <p className="text-xs text-slate-400">+ {money(p.setup_fee)} setup · {p.voice_minutes} min · {p.sms_messages} SMS</p>}
                <div className="mt-3">
                  {custom
                    ? <Button size="sm" variant="outline" className="w-full" onClick={contactSales} data-testid={`billing-contact-sales`}>Contact Sales</Button>
                    : current
                      ? <StatusBadge tone="green">Current</StatusBadge>
                      : <Button size="sm" className="w-full" disabled={busy === p.plan_type} onClick={() => select(p.plan_type)} data-testid={`billing-select-${p.plan_type}`}>{busy === p.plan_type ? "Redirecting…" : (stripe?.configured ? "Subscribe" : "Choose")}</Button>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function UsageBar({ icon: Icon, label, unit, m, testid }: any) {
  const tone = m.state === "exhausted" ? "bg-red-500" : m.state === "warn_90" ? "bg-amber-500" : m.state === "info_75" ? "bg-yellow-400" : "bg-emerald-500";
  return (
    <div data-testid={testid}>
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="flex items-center gap-2 text-slate-700"><Icon className="h-4 w-4 text-slate-400" /> {label}</span>
        <span className="text-slate-500" data-testid={`${testid}-count`}>
          {m.unlimited ? `${m.used} ${unit} · custom` : `${m.used} / ${m.allowance} ${unit} used (${m.percent}%)`}
        </span>
      </div>
      {!m.unlimited && (
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100">
          <div className={`h-full ${tone}`} style={{ width: `${Math.min(100, m.percent)}%` }} />
        </div>
      )}
      {m.state === "exhausted" && <p className="mt-1 text-xs text-red-500" data-testid={`${testid}-exhausted`}>Included allowance exhausted — overage {m.overage_units} {unit} (~{money(m.overage_estimate)}, not billed).</p>}
      {m.state === "warn_90" && <p className="mt-1 text-xs text-amber-600">Approaching your included limit.</p>}
      {m.state === "info_75" && <p className="mt-1 text-xs text-yellow-600">You've used 75% of your included {label.toLowerCase()}.</p>}
    </div>
  );
}
